import jsPDF from 'jspdf';
import moment from 'moment';

interface Exercise {
  name: string;
  shortDescription?: string;
  category?: string;
  subcategory?: string;
  objectives?: string;
  taskDevelopment?: string;
  images?: string[];
}

interface Section {
  name: string;
  sectionDuration?: number;
  exercises: Exercise[];
}

interface Training {
  name: string;
  trainingDuration?: number;
  sections: Section[];
}

interface PDFGeneratorProps {
  training: Training;
  userName: string;
  reduced?: boolean;
}

export const generatePDF = ({ training, userName, reduced = false }: PDFGeneratorProps) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number = 20) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to wrap text
  const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line: string) => {
      checkPageBreak();
      doc.text(line, x, yPosition);
      yPosition += fontSize / 2 + 2;
    });
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(training.name, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // User and Date
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Coach: ${userName}`, margin, yPosition);
  yPosition += 7;
  doc.text(`Date: ${moment().format('DD/MM/YYYY')}`, margin, yPosition);
  yPosition += 7;

  if (training.trainingDuration) {
    doc.text(`Duration: ${training.trainingDuration} min`, margin, yPosition);
    yPosition += 10;
  }

  // Line separator
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Sections
  training.sections.forEach((section, sectionIndex) => {
    checkPageBreak(30);

    // Section title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 84, 115); // Color azul
    addWrappedText(`${sectionIndex + 1}. ${section.name}`, margin, yPosition, contentWidth, 16);
    yPosition += 5;

    if (section.sectionDuration) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100);
      doc.text(`Duration: ${section.sectionDuration} min`, margin + 5, yPosition);
      yPosition += 8;
    }

    doc.setTextColor(0);

    // Exercises
    section.exercises.forEach((exercise, exerciseIndex) => {
      checkPageBreak(25);

      // Exercise name
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      addWrappedText(`${sectionIndex + 1}.${exerciseIndex + 1} ${exercise.name}`, margin + 5, yPosition, contentWidth - 5, 12);
      yPosition += 3;

      if (!reduced) {
        // Category
        if (exercise.category) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(80);
          doc.text(`Category: ${exercise.category}${exercise.subcategory ? ` - ${exercise.subcategory}` : ''}`, margin + 10, yPosition);
          yPosition += 6;
          doc.setTextColor(0);
        }

        // Short description
        if (exercise.shortDescription) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          addWrappedText(exercise.shortDescription, margin + 10, yPosition, contentWidth - 15, 10);
          yPosition += 3;
        }

        // Objectives
        if (exercise.objectives) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Objectives:', margin + 10, yPosition);
          yPosition += 5;
          doc.setFont('helvetica', 'normal');
          addWrappedText(exercise.objectives, margin + 15, yPosition, contentWidth - 20, 9);
          yPosition += 3;
        }

        // Task Development
        if (exercise.taskDevelopment) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Development:', margin + 10, yPosition);
          yPosition += 5;
          doc.setFont('helvetica', 'normal');
          addWrappedText(exercise.taskDevelopment, margin + 15, yPosition, contentWidth - 20, 9);
          yPosition += 3;
        }
      } else {
        // Reduced version - only short description
        if (exercise.shortDescription) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          addWrappedText(exercise.shortDescription, margin + 10, yPosition, contentWidth - 15, 9);
        }
      }

      yPosition += 8;
    });

    yPosition += 5;
  });

  // Footer on last page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  return doc;
};

export const downloadPDF = (props: PDFGeneratorProps) => {
  const doc = generatePDF(props);
  const fileName = `${props.training.name}_${moment().format('YYYY-MM-DD')}${props.reduced ? '_reduced' : ''}.pdf`;
  doc.save(fileName);
};
